import React, { useState, useEffect } from 'react';
import { format } from "d3-format";
import { timeFormat } from "d3-time-format";
import {
    discontinuousTimeScaleProviderBuilder,
    Chart,
    ChartCanvas,
    CurrentCoordinate,
    BarSeries,
    CandlestickSeries,
    LineSeries,
    OHLCTooltip,
    SingleValueTooltip,
    lastVisibleItemBasedZoomAnchor,
    XAxis,
    YAxis,
    CrossHairCursor,
    EdgeIndicator,
    MouseCoordinateX,
    MouseCoordinateY,
    ZoomButtons,
    SvgPathAnnotation,
    Annotate,
} from "react-financial-charts";
import { getLocalDate } from '../helpers/DateHelpers.js';


const HoverSpy = (props) => {
    const { onHover } = props;
    return <SingleValueTooltip
        yAccessor={(d) => {
            if (onHover && d) onHover(d);
            return 0;
        }}
        yLabel=""
        yDisplayFormat={() => ""}
        origin={[-10000, -10000]}
    />
}

function CandleStickChart(props) {

    const [quoteHistory, setQuoteHistory] = useState([]);
    const [tradeHistory, setTradeHistory] = useState([]);
    const [averagePrices, setAveragePrices] = useState({});
    const [upperIndicators, setUpperIndicators] = useState([]);
    const [lowerIndicators, setLowerIndicators] = useState([]);
    const [indicatorsMetadata, setIndicatorsMetadata] = useState([]);
    const [horizontalLines, setHorizontalLines] = useState([]);
    const [widthModifier, setWidthModifier] = useState(0);

    const lastHoveredDate = React.useRef(null);
    const lastHoveredDatum = React.useRef(null);

    const handleHover = (d) => {
        if (d) {
            lastHoveredDatum.current = d;
        }
        if (d && d.date) {
            lastHoveredDate.current = getLocalDate(d.date);
        }
    }

    const handleChartClick = () => {
        if (lastHoveredDate.current && props.onDateClicked) {
            props.onDateClicked(lastHoveredDate.current);
        }
    }

    const handleRightClick = (e) => {
        console.log(e);
        e.preventDefault();
        if (lastHoveredDatum.current) {
            setHorizontalLines(prev => [...prev, lastHoveredDatum.current.close]);
        }
    }

    useEffect(() => {
        (async function () {

            function calculateAveragePrices(quoteHistory, trades) {
                let avgPrice = {};
                quoteHistory.forEach(q => {
                    let totalCost = 0;
                    let totalShares = 0;
                    let date = getLocalDate(q.date);
                    trades.forEach(t => {
                        let purchaseDate = getLocalDate(t.purchase_date);
                        let sellDate = getLocalDate(t.sell_date);
                        if (purchaseDate < date && (sellDate >= date || t.sell_date === null || t.sell_date === undefined)) {
                            let allocation = t.quantity ? t.quantity : (t.allocation ? t.allocation : 1);
                            totalCost += t.purchase_price * allocation;
                            totalShares += allocation;
                        }
                    })
                    avgPrice[q.date] = totalCost / totalShares;
                })
                setAveragePrices(avgPrice)
            }

            if (props.trades) {
                setTradeHistory(props.trades);
                calculateAveragePrices(quoteHistory, props.trades);
            }
        })();
    }, [props.symbol, props.trades, quoteHistory, props.startDate]);

    useEffect(() => {
        (async function () {
            const processLowerSma = (history) => {
                if (props.lowerIndicatorSma && history && history.length > 0) {
                    const period = parseInt(props.lowerIndicatorSma, 10);
                    const lowerKeys = history[0].lower ? Object.keys(history[0].lower).filter(k => !k.endsWith('_sma')) : [];
                    history = history.map(d => ({ ...d, lower: d.lower ? { ...d.lower } : undefined }));

                    for (let i = 0; i < history.length; i++) {
                        if (i < period - 1) {
                            history[i].volume_sma = undefined;
                        } else {
                            let sum = 0;
                            let validCount = 0;
                            for (let j = 0; j < period; j++) {
                                let val = history[i - j].volume;
                                if (val !== undefined && val !== null && typeof val === 'number' && !isNaN(val)) {
                                    sum += val;
                                    validCount++;
                                }
                            }
                            history[i].volume_sma = validCount > 0 ? sum / validCount : undefined;
                        }
                    }

                    lowerKeys.forEach(key => {
                        for (let i = 0; i < history.length; i++) {
                            if (i < period - 1) {
                                history[i].lower[`${key}_sma`] = undefined;
                            } else {
                                let sum = 0;
                                let validCount = 0;
                                for (let j = 0; j < period; j++) {
                                    let val = history[i - j].lower[key];
                                    if (val !== undefined && val !== null) {
                                        if (typeof val === 'object') {
                                            val = val.value !== undefined ? val.value : (val.k !== undefined ? val.k : Object.values(val)[0]);
                                        }
                                        if (typeof val === 'number' && !isNaN(val)) {
                                            sum += val;
                                            validCount++;
                                        }
                                    }
                                }
                                history[i].lower[`${key}_sma`] = validCount > 0 ? sum / validCount : undefined;
                            }
                        }
                    });
                }
                return history;
            };

            if (props.data) {
                setUpperIndicators([]);
                setLowerIndicators([]);
                setQuoteHistory([]);
                setAveragePrices({});
                setHorizontalLines([]);

                let history = props.data;
                if (props.endDate) {
                    const end = new Date(props.endDate);
                    end.setHours(23, 59, 59, 999);
                    history = history.filter(d => getLocalDate(d.date) <= end);
                }

                if (props.metaData) {
                    setIndicatorsMetadata(props.metaData);
                } else {
                    setIndicatorsMetadata([]);
                }

                if (history && history.length > 0) {
                    setUpperIndicators(history[0].upper ? Object.keys(history[0].upper).filter(k => !k.endsWith('_sma')) : []);
                    setLowerIndicators(history[0].lower ? Object.keys(history[0].lower).filter(k => !k.endsWith('_sma')) : []);
                }

                setQuoteHistory(processLowerSma(history));
                return;
            }

            if (props.symbol) {
                setUpperIndicators([]);
                setLowerIndicators([]);
                setQuoteHistory([]);
                setAveragePrices({});
                setHorizontalLines([]);

                let date = new Date();
                if (props.endDate)
                    date = new Date(props.endDate);

                let url = `/api/quoteHistoryWithIndicators?symbol=` + props.symbol +
                    "&startDate=" + (props.startDate ? props.startDate : "2014-1-1") +
                    "&endDate=" + date.toISOString().split('T')[0];
                if (props.timeframe === 2)
                    url += "&timeframe=2";
                let payload = { indicators: [] };
                if (props.showMovingAverage)
                    payload.indicators.push(
                        { type: 'sma', id: 'sma20', config: { period: 20 } },
                        { type: 'sma', id: 'sma50', config: { period: 50 } },
                        { type: 'sma', id: 'sma200', config: { period: 200 } }
                    );
                if (props.indicators) {
                    payload.indicators.push(...props.indicators);
                }
                console.log("indicators payload:", payload);
                let response = await (await fetch(url, { method: 'post', body: JSON.stringify(payload) })).json();
                console.log(props.symbol, response);
                if (response.error) {
                    console.error(response.error);
                    return;
                }
                if (response.metaData && response.metaData.indicators && Array.isArray(response.metaData.indicators))
                    setIndicatorsMetadata(response.metaData.indicators);
                else
                    setIndicatorsMetadata([]);

                let history = response.data;
                if (!history)
                    history = [];

                if (history && history.length > 0) {
                    setUpperIndicators(history[0].upper ? Object.keys(history[0].upper).filter(k => !k.endsWith('_sma')) : []);
                    setLowerIndicators(history[0].lower ? Object.keys(history[0].lower).filter(k => !k.endsWith('_sma')) : []);
                } else {
                    setUpperIndicators([]);
                    setLowerIndicators([]);
                }

                setQuoteHistory(processLowerSma(history));
            }
        })();
    }, [props.updates, props.symbol, props.startDate, props.timeframe, props.showMovingAverage, props.showVolume, props.indicators, props.endDate, props.data, props.metaData, props.lowerIndicatorSma]);
    const baseWidth = props.width ? props.width : 900;
    const width = baseWidth + widthModifier;
    const widthRatio = width / baseWidth;
    let height = (props.height ? props.height : baseWidth * .6) * widthRatio;
    height += lowerIndicators.length * 80 + (props.showVolume ? 80 : 0);
    const margin = { left: 0, right: 48, top: 0, bottom: 24 };
    const lineColors = ["#000000", "#000080", "#800000", "#006400", "#808000", "#4B0082", "#8B4513", "#2F4F4F", "#8B008B", "#008B8B", "#00008B", "#A52A2A", "#191970", "#B22222", "#556B2F", "#483D8B", "#2E8B57", "#D2691E", "#800080", "#696969", "#8B0000", "#5F9EA0"];
    const ScaleProvider = discontinuousTimeScaleProviderBuilder().inputDateAccessor(
        (d) => getLocalDate(d.date)
    );
    const { data, xScale, xAccessor, displayXAccessor } = ScaleProvider(
        quoteHistory
    );
    const pricesDisplayFormat = format(".2~f");
    let initialVisibleBars = props.initialVisibleBars ? props.initialVisibleBars : 66;
    let minIndex = Math.max(0, data.length - initialVisibleBars);
    let maxIndex = data.length - 1;
    let padding = 5;

    if (props.showDate) {
        let targetDate = getLocalDate(props.showDate).getTime();
        let targetIndex = data.length - 1;
        for (let i = 0; i < data.length; i++) {
            if (getLocalDate(data[i].date).getTime() >= targetDate) {
                targetIndex = i;
                break;
            }
        }
        let halfWindow = Math.floor(initialVisibleBars / 2);
        minIndex = Math.max(0, targetIndex - halfWindow);
        maxIndex = Math.min(data.length - 1, targetIndex + halfWindow);
        padding = 0;
    }

    const max = xAccessor(data[maxIndex]);
    const min = xAccessor(data[minIndex]);
    const xExtents = [min, max + padding];

    const gridHeight = height - margin.top - margin.bottom;

    const lowerIndicatorChartHeight = 80;
    const totalLowerIndicatorHeight = lowerIndicators.length * lowerIndicatorChartHeight;
    const barChartHeight = props.showVolume ? lowerIndicatorChartHeight : 0;
    const mainChartHeight = gridHeight - barChartHeight - totalLowerIndicatorHeight;
    const barChartOrigin = (_, h) => [0, mainChartHeight];
    const dateTimeFormat = "%d %b";
    const timeDisplayFormat = timeFormat(dateTimeFormat);

    const barChartExtents = (data) => {
        return [data.volume, data.volume_sma];
    };

    const candleChartExtents = (data) => {
        return [data.high * 1.02, data.low * .98];
    };

    const yEdgeIndicator = (data) => {
        return data.close;
    };

    const volumeColor = (data) => {
        return data.close > data.open
            ? "rgba(38, 166, 154, 0.3)"
            : "rgba(239, 83, 80, 0.3)";
    };

    const volumeSeries = (data) => {
        return data.volume;
    };

    const openCloseColor = (data) => {
        return data.close > data.open ? "#26a69a" : "#ef5350";
    };


    const annotationsSet = React.useMemo(() => {
        if (!props.annotations || props.annotations.length === 0) return null;
        const set = new Set();
        props.annotations.forEach(a => {
            if (a && a.date) {
                const d = getLocalDate(a.date);
                set.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
            }
        });
        return set;
    }, [props.annotations]);

    const buyDatesSet = React.useMemo(() => {
        if (!tradeHistory || tradeHistory.length === 0) return null;
        const set = new Set();
        tradeHistory.forEach(trade => {
            if (!trade.is_short && trade.purchase_date) {
                const buyDate = getLocalDate(trade.purchase_date);
                set.add(`${buyDate.getFullYear()}-${buyDate.getMonth()}-${buyDate.getDate()}`);
            }
            if (trade.is_short && trade.sell_date) {
                const coverDate = getLocalDate(trade.sell_date);
                set.add(`${coverDate.getFullYear()}-${coverDate.getMonth()}-${coverDate.getDate()}`);
            }
        });
        return set;
    }, [tradeHistory]);

    const sellDatesSet = React.useMemo(() => {
        if (!tradeHistory || tradeHistory.length === 0) return null;
        const set = new Set();
        tradeHistory.forEach(trade => {
            if (!trade.is_short && trade.sell_date) {
                const sellDate = getLocalDate(trade.sell_date);
                set.add(`${sellDate.getFullYear()}-${sellDate.getMonth()}-${sellDate.getDate()}`);
            }
            if (trade.is_short && trade.purchase_date) {
                const shortDate = getLocalDate(trade.purchase_date);
                set.add(`${shortDate.getFullYear()}-${shortDate.getMonth()}-${shortDate.getDate()}`);
            }
        });
        return set;
    }, [tradeHistory]);

    function shouldShowBuyAnnotation(data) {
        if (!buyDatesSet || !data || !data.date) return false;
        const date = getLocalDate(data.date);
        return buyDatesSet.has(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
    }

    function shouldShowSellAnnotation(data) {
        if (!sellDatesSet || !data || !data.date) return false;
        const date = getLocalDate(data.date);
        return sellDatesSet.has(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
    }

    function shouldShowAnnotation(data) {
        if (!annotationsSet || !data || !data.date) return false;
        const date = getLocalDate(data.date);
        return annotationsSet.has(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`);
    }

    const svgBuyAnnotationProps = {
        fill: "#2196f3",
        path: () =>
            "M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z",
        pathWidth: 12,
        pathHeight: 22,
        tooltip: "Buy",
        y: ({ yScale, datum }) => yScale(datum.close),
    }

    const svgSellAnnotationProps = {
        fill: "#000000",
        path: () =>
            "M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z",
        pathWidth: 12,
        pathHeight: 22,
        tooltip: "Sell",
        y: ({ yScale, datum }) => yScale(datum.close),
    }

    const svgCustomAnnotationProps = {
        fill: "#ffd9009f",
        path: () =>
            "M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z",
        pathWidth: 12,
        pathHeight: 22,
        tooltip: "Selected",
        y: ({ yScale, datum }) => yScale(datum.close),
    }

    function getAveragePrice(item) {
        if (item)
            return averagePrices[item.date];
        else
            return null;
    }
    return (
        <>
            {
                quoteHistory && quoteHistory.length ?
                    <>
                        <div onClick={handleChartClick} onContextMenu={handleRightClick} style={{ position: 'relative', display: 'inline-block' }}>
                            <div style={{ position: 'absolute', bottom: '10px', left: '10px', zIndex: 10 }}>
                                <button onClick={(e) => { e.stopPropagation(); setWidthModifier(m => m + 50); }} style={{ padding: '0 8px', marginRight: '4px', cursor: 'pointer' }}>+</button>
                                <button onClick={(e) => { e.stopPropagation(); setWidthModifier(m => m - 50); }} style={{ padding: '0 8px', cursor: 'pointer' }}>-</button>
                            </div>
                            <ChartCanvas
                                height={height}
                                ratio={3}
                                width={width}
                                margin={margin}
                                data={data}
                                displayXAccessor={displayXAccessor}
                                seriesName="Data"
                                xScale={xScale}
                                xAccessor={xAccessor}
                                xExtents={xExtents}
                                zoomAnchor={lastVisibleItemBasedZoomAnchor}
                            >
                                {props.showVolume ?
                                    <Chart
                                        id={2}
                                        height={barChartHeight}
                                        origin={barChartOrigin}
                                        yExtents={barChartExtents}
                                    >
                                        <BarSeries fillStyle={volumeColor} yAccessor={volumeSeries} />
                                        {props.lowerIndicatorSma && (
                                            <LineSeries yAccessor={d => d.volume_sma} strokeStyle="#000000" strokeWidth={1} />
                                        )}
                                    </Chart>
                                    :
                                    null
                                }
                                <Chart id={3} height={mainChartHeight} yExtents={candleChartExtents}>
                                    <XAxis showGridLines ticks={8} showTickLabel={true} />
                                    <YAxis showGridLines ticks={8} tickFormat={pricesDisplayFormat} />
                                    <CandlestickSeries />
                                    {horizontalLines.map((price, i) => (
                                        <LineSeries key={i} yAccessor={() => price} strokeStyle="#000000" strokeDasharray="LongDash" />
                                    ))}
                                    <HoverSpy onHover={handleHover} />
                                    <Annotate with={SvgPathAnnotation} usingProps={svgBuyAnnotationProps} when={shouldShowBuyAnnotation} />
                                    <Annotate with={SvgPathAnnotation} usingProps={svgSellAnnotationProps} when={shouldShowSellAnnotation} />
                                    <Annotate with={SvgPathAnnotation} usingProps={svgCustomAnnotationProps} when={shouldShowAnnotation} />
                                    {upperIndicators && upperIndicators.length && upperIndicators.map((indicator, index) => {
                                        const sampleDataPoint = data.find(d => d && d.upper && d.upper[indicator] !== undefined && d.upper[indicator] !== null);
                                        const indicatorValue = sampleDataPoint ? sampleDataPoint.upper[indicator] : null;
                                        const isObject = indicatorValue && typeof indicatorValue === 'object';
                                        const isArray = indicatorValue && typeof indicatorValue === 'object' && Array.isArray(indicatorValue);

                                        if (isArray) {
                                            const lastValue = data[data.length - 1].upper[indicator];
                                            console.log("keyLevels", lastValue);
                                            // draw a horizontal line at each value in the array
                                            return lastValue.map((value, i) => (
                                                <React.Fragment key={`${indicator}_${i}`}>
                                                    <LineSeries yAccessor={() => value} strokeStyle={lineColors[(index + i) % lineColors.length]} />
                                                    <CurrentCoordinate yAccessor={() => value} fillStyle={lineColors[(index + i) % lineColors.length]} />
                                                </React.Fragment>
                                            ));
                                        } else if (isObject) {
                                            return Object.keys(indicatorValue).map((key, i) => (
                                                <React.Fragment key={`${indicator}_${key}`}>
                                                    <LineSeries yAccessor={d => d?.upper?.[indicator]?.[key]} strokeStyle={lineColors[(index + i) % lineColors.length]} />
                                                    <CurrentCoordinate yAccessor={d => d?.upper?.[indicator]?.[key]} fillStyle={lineColors[(index + i) % lineColors.length]} />
                                                </React.Fragment>
                                            ));
                                        } else {
                                            return <React.Fragment key={indicator}>
                                                <LineSeries yAccessor={d => d?.upper?.[indicator]} strokeStyle={lineColors[index % lineColors.length]} />
                                                <CurrentCoordinate yAccessor={d => d?.upper?.[indicator]} fillStyle={lineColors[index % lineColors.length]} />
                                            </React.Fragment>
                                        }
                                    })}
                                    {props.showAveragePrice ?
                                        <>
                                            <LineSeries yAccessor={getAveragePrice} strokeStyle={"#000000"} strokeWidth={2} />
                                            <CurrentCoordinate
                                                yAccessor={getAveragePrice}
                                                fillStyle={"#000000"}
                                            />
                                        </>
                                        :
                                        null
                                    }
                                    <MouseCoordinateY
                                        rectWidth={margin.right}
                                        displayFormat={pricesDisplayFormat}
                                    />
                                    <EdgeIndicator
                                        itemType="last"
                                        rectWidth={margin.right}
                                        fill={openCloseColor}
                                        lineStroke={openCloseColor}
                                        displayFormat={pricesDisplayFormat}
                                        yAccessor={yEdgeIndicator}
                                    />

                                    {props.hideZoomButtons ? null : <ZoomButtons />}
                                    {props.hideTooltip ? null :
                                        <OHLCTooltip origin={[8, 16]} fontWeight={"600"} />
                                    }
                                </Chart>
                                {lowerIndicators.map((indicator, index) => {
                                    const meta = indicatorsMetadata.find(i => (i.id || i.type) === indicator);
                                    const sampleDataPoint = data.find(d => d.lower && d.lower[indicator] !== undefined && d.lower[indicator] !== null);
                                    const indicatorValue = sampleDataPoint ? sampleDataPoint.lower[indicator] : null;
                                    const isObject = indicatorValue && typeof indicatorValue === 'object';

                                    const yExtents = (meta && meta.upperBound !== undefined && meta.lowerBound !== undefined)
                                        ? [meta.lowerBound, meta.upperBound]
                                        : d => {
                                            const val = d.lower?.[indicator];
                                            if (val === undefined || val === null) return undefined;
                                            return (typeof val === 'object' ? Object.values(val) : [val]).flatMap(v => [v * 1.05, v * 0.95]);
                                        };
                                    const lowerBuyProps = {
                                        ...svgBuyAnnotationProps,
                                        y: ({ yScale, datum }) => {
                                            let val = datum.lower?.[indicator];
                                            if (val && typeof val === 'object') val = Object.values(val)[0];
                                            return yScale(val);
                                        }
                                    };
                                    const lowerSellProps = {
                                        ...svgSellAnnotationProps,
                                        y: ({ yScale, datum }) => {
                                            let val = datum.lower?.[indicator];
                                            if (val && typeof val === 'object') val = Object.values(val)[0];
                                            return yScale(val);
                                        }
                                    };
                                    const lowerCustomProps = {
                                        ...svgCustomAnnotationProps,
                                        y: ({ yScale, datum }) => {
                                            let val = datum.lower?.[indicator];
                                            if (val && typeof val === 'object') val = Object.values(val)[0];
                                            return yScale(val);
                                        }
                                    };
                                    return <Chart
                                        key={indicator}
                                        id={4 + index}
                                        yExtents={yExtents}
                                        height={lowerIndicatorChartHeight}
                                        origin={(_, h) => [0, mainChartHeight + barChartHeight + (index * lowerIndicatorChartHeight)]}
                                        padding={{ top: 10, bottom: 10 }}
                                    >
                                        <XAxis showGridLines gridLinesStrokeStyle="#e0e3eb" />
                                        <YAxis ticks={4} tickFormat={pricesDisplayFormat} />
                                        <MouseCoordinateX displayFormat={timeDisplayFormat} />
                                        <MouseCoordinateY
                                            rectWidth={margin.right}
                                            displayFormat={pricesDisplayFormat}
                                        />
                                        {isObject ?
                                            Object.keys(indicatorValue).map((key, i) => (
                                                <React.Fragment key={key}>
                                                    <LineSeries yAccessor={d => d.lower?.[indicator]?.[key]} strokeStyle={lineColors[(upperIndicators.length + index + i) % lineColors.length]} />
                                                    <SingleValueTooltip
                                                        yLabel={key === 'value' ? indicator.toUpperCase() : key.toUpperCase()}
                                                        yAccessor={d => d.lower?.[indicator]?.[key]}
                                                        yDisplayFormat={format(".2f")}
                                                        origin={[8 + (i * 50), 16]}
                                                    />
                                                </React.Fragment>
                                            ))
                                            :
                                            <>
                                                <LineSeries yAccessor={d => d.lower?.[indicator]} strokeStyle={lineColors[(upperIndicators.length + index) % lineColors.length]} />
                                                <SingleValueTooltip
                                                    yLabel={indicator.toUpperCase()}
                                                    yAccessor={d => d.lower?.[indicator]}
                                                    yDisplayFormat={format(".2f")}
                                                    origin={[8, 16]}
                                                />
                                            </>
                                        }
                                        {props.lowerIndicatorSma && (
                                            <LineSeries yAccessor={d => d.lower?.[`${indicator}_sma`]} strokeStyle="#000000" strokeWidth={1} />
                                        )}
                                        <Annotate with={SvgPathAnnotation} usingProps={lowerBuyProps} when={shouldShowBuyAnnotation} />
                                        <Annotate with={SvgPathAnnotation} usingProps={lowerSellProps} when={shouldShowSellAnnotation} />
                                        <Annotate with={SvgPathAnnotation} usingProps={lowerCustomProps} when={shouldShowAnnotation} />
                                    </Chart>
                                })}
                                <CrossHairCursor />
                            </ChartCanvas>
                        </div>
                    </>
                    :
                    null
            }

        </>
    );
}

export default CandleStickChart;